import {
  type ScaleOrdinal,
  scaleQuantize,
  type ScaleQuantize,
  type ScaleThreshold,
} from 'd3-scale';
import { isEmpty, uniqBy } from 'lodash-es';
import { type PropsWithChildren } from 'react';

import { ColorPalette } from '../../core/types/color-palette.js';
import {
  ColoredRange,
  isColoredRangeArray,
} from '../../core/types/colored-range.js';
import { FormatterWithPrecision } from '../../core/types/formatter.js';
import { buildQuantizeScaleFromColorPalette } from '../../core/utils/build-quantize-scale-from-color-palette.js';
import { buildThresholdScaleFromRanges } from '../../core/utils/build-threshold-scale-from-ranges.js';
import { defaultUnitFormatter } from '../../core/utils/defaultUnitFormatter.js';
import { getTickFormatterMethod } from '../../core/utils/get-tick-formatter-method.js';
import { isValidColorPalette } from '../../core/utils/is-valid-color-palette.js';
import { DEFAULT_HONEYCOMB_TILE_COLOR } from '../constants.js';
import { HoneycombBaseScaleContext } from '../context/honeycomb-base-scale.context.js';
import { HoneycombFilteredColorSchemeContext } from '../context/honeycomb-filtered-color-scheme.context.js';
import { HoneycombTickFormatter } from '../context/honeycomb-tick-formatter.context.js';
import { useHoneycombColorScheme } from '../hooks/use-honeycomb-color-scheme.context.js';
import { useHoneycombData } from '../hooks/use-honeycomb-data.js';
import { useHoneycombExtent } from '../hooks/use-honeycomb-extent.js';
import { useHoneycombFormatterConfig } from '../hooks/use-honeycomb-formatter-config.js';
import type {
  HoneycombTileCategoricalData,
  HoneycombTileNumericData,
} from '../types/honeycomb.js';
import {
  buildCategoricalScaleFromColorPalette,
  buildCategoricalScaleFromCustomPalette,
} from '../utils/build-categorical-scale.js';
import { filterColoredRange } from '../utils/filter-colored-range.js';
import { getDataExtent } from '../utils/get-data-extent.js';
import { isHoneycombTileCategoricalData } from '../utils/is-honeycomb-tile-categorical-data.js';
import { isHoneycombTileNumericData } from '../utils/is-honeycomb-tile-numeric-data.js';

const buildLegendTicks = (scale: ScaleQuantize<string>) => [
  ...scale.domain(),
  ...(scale.thresholds ? scale.thresholds() : []),
];

export const HoneycombBaseScaleProvider = ({ children }: PropsWithChildren) => {
  let scale:
    | ScaleQuantize<string>
    | ScaleThreshold<number, string>
    | ScaleOrdinal<string, string, string> = scaleQuantize<string>();
  const data = useHoneycombData();
  const colorScheme = useHoneycombColorScheme();
  const { formatter, unit } = useHoneycombFormatterConfig();
  const validColorPalette = isValidColorPalette(colorScheme);
  const configuredExtent = useHoneycombExtent();
  let tickFormatter: FormatterWithPrecision = defaultUnitFormatter(unit);

  let filteredColorScheme:
    | ColorPalette
    | { [key: string]: string }
    | ColoredRange[]
    | string[] = colorScheme;

  if (!isEmpty(data)) {
    if (
      isHoneycombTileCategoricalData(data[0]) &&
      !Array.isArray(colorScheme)
    ) {
      const filteredScheme = uniqBy(data, 'value');
      const categories = (filteredScheme as HoneycombTileCategoricalData[]).map(
        (tileData) => {
          return tileData.value;
        },
      );

      if (validColorPalette) {
        scale = buildCategoricalScaleFromColorPalette(colorScheme, categories);
      } else {
        scale = buildCategoricalScaleFromCustomPalette(colorScheme, categories);
      }
    }
    if (isHoneycombTileNumericData(data[0])) {
      const [min, max] = getDataExtent(
        data as HoneycombTileNumericData[],
        configuredExtent,
      );

      if (isColoredRangeArray(colorScheme)) {
        filteredColorScheme = filterColoredRange(
          colorScheme,
          configuredExtent,
          { min, max },
        );
        scale = buildThresholdScaleFromRanges(
          filteredColorScheme,
          DEFAULT_HONEYCOMB_TILE_COLOR,
        );
      } else {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        scale = buildQuantizeScaleFromColorPalette(min!, max!, colorScheme);
      }

      const domain = buildLegendTicks(scale as ScaleQuantize<string>);

      tickFormatter = getTickFormatterMethod(domain, formatter, unit);
    }
  }

  return (
    <HoneycombBaseScaleContext.Provider value={scale}>
      <HoneycombTickFormatter.Provider value={tickFormatter}>
        <HoneycombFilteredColorSchemeContext.Provider
          value={filteredColorScheme}
        >
          {children}
        </HoneycombFilteredColorSchemeContext.Provider>
      </HoneycombTickFormatter.Provider>
    </HoneycombBaseScaleContext.Provider>
  );
};
